import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { IconSparkles } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

export interface CopilotProposalBannerProps {
  /** What the agent is proposing, in the user's terms. */
  message: string;
  /** The proposal's own one-sentence summary, shown above `message`. */
  summary?: string;
  /** Removals read destructive: a rose ground and a warning confirm button. */
  isDestructive?: boolean;
  discardLabel: string;
  confirmLabel: string;
  onDiscard: () => void;
  onConfirm: () => void;
  /** Both decisions are held while one is in flight. */
  isDisabled?: boolean;
  className?: string;
}

/**
 * The banner above an editor when Copilot has an unresolved proposal for what
 * it edits, carrying the two decisions the user can make about it. Shared by
 * the playbook's criterion, section, and outcome panes, the voice agent's
 * instruction sections and test cases, and the knowledge base document
 * editor, and by both proposal shapes: an edit previews its changes in the
 * form below, while a removal has nothing to preview and leaves the form as it
 * is.
 *
 * Users see "suggestion"; the code says "proposal", after the row it resolves.
 */
export const CopilotProposalBanner: FC<CopilotProposalBannerProps> = ({
  message,
  summary,
  isDestructive = false,
  discardLabel,
  confirmLabel,
  onDiscard,
  onConfirm,
  isDisabled = false,
  className,
}) => (
  <div
    className={twMerge(
      "@container shrink-0 rounded-lg px-3 py-2",
      isDestructive ? "bg-rose-50" : "bg-indigo-50",
      className,
    )}
  >
    <div className="flex flex-col gap-2 @xl:flex-row @xl:items-center @xl:justify-between">
      <div
        className={twMerge(
          "flex items-center gap-2 text-sm",
          summary !== undefined && "items-start",
          isDestructive ? "text-rose-900" : "text-indigo-900",
        )}
      >
        <IconSparkles
          className={twMerge(
            "size-4 shrink-0",
            summary !== undefined && "mt-0.5",
            isDestructive ? "text-rose-500" : "text-indigo-500",
          )}
        />
        {summary === undefined ? (
          <span>{message}</span>
        ) : (
          <div className="min-w-0">
            <p>{summary}</p>
            <p
              className={twMerge(
                "mt-0.5 text-xs",
                isDestructive ? "text-rose-700" : "text-indigo-700",
              )}
            >
              {message}
            </p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          variant={ButtonVariant.Tertiary}
          size={Size.SMALL}
          onClick={onDiscard}
          isDisabled={isDisabled}
        >
          {discardLabel}
        </Button>
        <Button
          variant={
            isDestructive ? ButtonVariant.Warning : ButtonVariant.Primary
          }
          size={Size.SMALL}
          onClick={onConfirm}
          isDisabled={isDisabled}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);
